/* eslint-disable react/no-multi-comp */
import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import autobind from 'autobind-decorator';

import {cn} from 'spa/css';

import LoadingIndicator from 'spa/Components/LoadingIndicator';
import clickOutside from 'spa/Components/OnClickOutside';

const KEY_CODES = {
  UP: 38,
  DOWN: 40,
  ENTER: 13,
  ESCAPE: 27,
};

function isFixed(element) {
  while (element) {
    if (window.getComputedStyle(element).position === 'fixed') {
      return true;
    }
    element = element.parentElement;
  }
  return false;
}

@clickOutside
class OptionsList extends React.Component {
  static propTypes = {
    align: PropTypes.string,
    className: PropTypes.string,
    dark: PropTypes.bool,
    error: PropTypes.string,
    footer: PropTypes.object,
    fromTop: PropTypes.bool,
    fromTopClassName: PropTypes.string,
    hideSelected: PropTypes.bool,
    isLoading: PropTypes.bool,
    maxHeight: PropTypes.number,
    name: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
      subtext: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]).isRequired,
      matched_substrings: PropTypes.arrayOf(PropTypes.shape({
        length: PropTypes.number.isRequired,
        offset: PropTypes.number.isRequired,
      }).isRequired),
      selected: PropTypes.bool,
    })).isRequired,
    onClose: PropTypes.func,
    onHighlight: PropTypes.func,
    onSelect: PropTypes.func,
    selectFirstOptionOnEnterKey: PropTypes.bool,
    warning: PropTypes.string,
    zIndex: PropTypes.number,
  };

  state = {};

  UNSAFE_componentWillMount() {
    if (typeof document !== 'undefined') {
      document.body.addEventListener('keydown', this.onKeyDown);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.options !== nextProps.options) {
      const option = nextProps.options.find(_option => _option.selected);

      if (this.props.onHighlight) this.props.onHighlight(option);

      this.setState({
        highlighted: (option || {}).value,
      });
    }
  }

  componentWillUnmount() {
    if (typeof document !== 'undefined') {
      document.body.removeEventListener('keydown', this.onKeyDown);
    }
  }

  onMouseEnter = (option) => {
    if (this.props.onHighlight) this.props.onHighlight(option);
    this.setState({
      highlighted: option.value,
    });
  };

  onMouseLeaveList = () => {
    if (this.props.onHighlight) this.props.onHighlight(null);
    this.setState({
      highlighted: null,
    });
  };

  onKeyDown = (event) => {
    const options = this.props.options.filter(option => !(option.selected && this.props.hideSelected));

    let change = 0;
    switch (event.keyCode) {
    case KEY_CODES.UP:
      // select prev
      change = -1;
      break;
    case KEY_CODES.DOWN:
      // select next
      change = 1;
      break;
    case KEY_CODES.ENTER:
      // highlight should become selected
      if (this.state.highlighted == null) {
        if (this.props.selectFirstOptionOnEnterKey && options.length) {
          this.props.onSelect(options[0].value);
        }
      } else {
        this.props.onSelect(this.state.highlighted);
      }
      event.preventDefault();
      return false;
    case KEY_CODES.ESCAPE:
      // just close
      this.props.onClose();
      event.preventDefault();
      return false;
    default: return null;
    }

    const highlighted = options.find(option => option.value === this.state.highlighted);
    let index = options.indexOf(highlighted);

    index += change;
    index %= options.length;
    if (index < 0) index += options.length;

    if (this.props.onHighlight) this.props.onHighlight(options[index]);

    this.setState({
      highlighted: (options[index] || {}).value,
    });

    event.preventDefault();

    return false;
  };

  onClick(option, event) {
    this.props.onSelect(option.value, event);
  }

  render() {
    /* eslint-disable no-unused-vars */
    const {
      options, dark, onHighlight, isLoading, className = '',
      fromTopClassName,
      onClose, hideSelected,
      warning,
      error,
      footer,
      selectFirstOptionOnEnterKey,
      align,
      fromTop,
      zIndex,
      maxHeight,
      ...rest
    } = this.props;
    /* eslint-enable no-unused-vars */

    let message;
    if (isLoading) {
      message = (
        <div style={{padding: '20px'}}>
          <LoadingIndicator style={{color: '#333'}} />
        </div>
      );
    } else if (error) {
      message = (
        <div style={{padding: '10px'}} className="text-danger">
          {error}
        </div>
      );
    } else if (warning) {
      message = (
        <div style={{padding: '10px'}}>
          {warning}
        </div>
      );
    }

    if (!message && options.length === 0) return null;

    return (
      <div
        {...rest}
        className={`options-list ${className} ${fromTopClassName} options-list--align-${align}`}
        onMouseLeave={this.onMouseLeaveList}
        style={{maxHeight: maxHeight ? `${maxHeight}px` : 'auto', overflowY: maxHeight ? 'auto' : 'visible' }}
      >
        {message}
        {options.map(this.renderOption)}
        {this.props.footer}
      </div>
    );
  }

  renderOption = (option) => {
    if (option.selected && this.props.hideSelected) return null;

    const highlighted = this.state.highlighted === option.value;

    const {name = '', subtext = '', matched_substrings} = option;
    const content = [];
    if (option.matched_substrings) {
      let iMatched = 0;
      for (let i = 0; i < name.length;) {
        const match = matched_substrings[iMatched];
        if (match) {
          if (match.offset === i) {
            content.push(<b key={`name-${i}`}>{name.substr(i, match.length)}</b>);
            i += match.length;
            ++iMatched;
          } else {
            const end = match.offset;
            content.push(<span key={`name-${i}`}>{name.substr(i, end - i)}</span>);
            i = end;
          }
        } else {
          content.push(<span key={`name-${i}`}>{name.substr(i, name.length - i)}</span>);
          i = name.length;
          break;
        }
      }
      if (subtext) {
        if (
          matched_substrings[iMatched - 1] &&
          (
            matched_substrings[iMatched - 1].offset +
            matched_substrings[iMatched - 1].length >
            name.length
          )
        ) {
          --iMatched;
        }
        const subtextOffset = name.length;
        for (let i = 0; i < subtext.length;) {
          const match = matched_substrings[iMatched];
          if (match) {
            let {length, offset} = match;
            offset -= subtextOffset;

            if (offset < 0) {
              length += offset;
              offset = 0;
            }

            if (offset === i) {
              content.push(<small key={`subtext-${i}`}><b>{subtext.substr(i, length)}</b></small>);
              i += length;
              ++iMatched;
            } else {
              const end = offset;
              content.push(<small key={`subtext-${i}`}>{subtext.substr(i, end - i)}</small>);
              i = end;
            }
          } else {
            content.push(<small key={`subtext-${i}`}>{subtext.substr(i, subtext.length - i)}</small>);
            i = subtext.length;
            break;
          }
        }
      }
    } else {
      content.push(<span key="name" data-qa={`option-${option.value}-text`}>{name}</span>);
      if (subtext) {
        content.push(<small className="options-list-option__subtext" key="subtext" data-qa={`option-${option.value}-sub-text`}>{subtext}</small>);
      }
    }

    return (
      <div
        data-qa={`option-${option.value}`}
        key={option.value}
        onClick={this.onClick.bind(this, option)}
        onMouseEnter={() => this.onMouseEnter(option)}
        className={cn([
          `options-list-option`,
          option.selected ? 'options-list-option--selected' : '',
          highlighted ? 'options-list-option--highlighted' : '',
          option.className,
        ])}>
        {content}
      </div>
    );
  };
}

class OptionsListContainer extends React.Component {
  static propTypes = {
    hide: PropTypes.bool,
    children: PropTypes.any,
    containerStyle: PropTypes.object,
    containerClassName: PropTypes.string,
    name: PropTypes.string.isRequired,
    noExpandX: PropTypes.bool,
    onClose: PropTypes.func,
    onClick: PropTypes.func,

    width: PropTypes.number,
    zIndex: PropTypes.number,
    align: PropTypes.oneOf(['left', 'center', 'right']),
    fromTop: PropTypes.bool,
  };

  static defaultProps = {
    align: 'left',
    zIndex: 21,
  };

  componentDidMount() {
    if (!this.props.hide) {
      this.onShow();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.hide && !this.props.hide) {
      this.onHide();
    } else if (!nextProps.hide && this.props.hide) {
      this.onShow();
    }
  }

  componentDidUpdate() {
    if (!this.props.hide) {
      this.renderContent();
    }
  }

  componentWillUnmount() {
    if (!this.props.hide) {
      this.onHide();
    }
  }

  onClickOutside = (event) => {
    if (this.props.hide) return;

    if (this.props.onClose) {
      if (this.content && this.content.contains) {
        if (
          this.content === event.target ||
          this.content.contains(event.target)
        ) {
          return;
        }
      }

      this.props.onClose();
    }
  };

  onShow() {
    this.containerElement = document.createElement('div');
    document.body.appendChild(this.containerElement);

    this.isFixed = isFixed(this.content);

    Object.assign(this.containerElement.style, {
      position: this.isFixed ? 'fixed' : 'absolute',
      zIndex: this.props.zIndex,
    });

    window.addEventListener('resize', this.onResize);
    window.addEventListener('load', this.onResize);
    this.onResize();
    this.renderContent();
  }
  onHide() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('load', this.onResize);
    ReactDOM.unmountComponentAtNode(this.containerElement);
    this.containerElement.parentNode.removeChild(this.containerElement);
  }

  onResize = () => {
    let windowRect = document.documentElement.getBoundingClientRect();
    const contentRect = this.content.getBoundingClientRect();

    if (this.isFixed) {
      windowRect = {
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
        width: windowRect.width,
        height: windowRect.height,
      };
    }

    Object.assign(this.containerElement.style, {
      top: `${contentRect.top + contentRect.height - windowRect.top}px`,
      right: `auto`,
      left: `auto`,
      width: this.props.width ? `${this.props.width}px` : 'auto',
      minWidth: `${contentRect.width}px`,
      maxWidth: this.props.noExpandX ? `${contentRect.width}px` : 'none',
      display: 'none',
    });

    switch (this.props.align) {
    case 'right':
      this.containerElement.style.right = `${windowRect.right - contentRect.right}px`;
      break;
    case 'center':
      if (!this.props.width) {
        throw new Error('You need to specify the width when using align="center" in <OptionsList>');
      }
      // below 600px wide we want the country select popup to be full width
      this.containerElement.style.left = windowRect && windowRect.width <= 600 ? '0' : `${
        contentRect.left - windowRect.left - this.props.width / 2 +
        contentRect.width / 2
      }px`;

      this.containerElement.style.right = windowRect && windowRect.width <= 600 && '0';
      this.containerElement.style.width = windowRect && windowRect.width <= 600 && '100%';
      break;
    case 'left':
    default:
      this.containerElement.style.left = `${contentRect.left - windowRect.left}px`;
      break;
    }
  };

  @autobind contentRef(content) { this.content = content; }

  render() {
    return (
      <div
        data-qa={`${this.props.name}-options-list-trigger`}
        ref={this.contentRef}
        onClick={this.props.onClick}
        style={this.props.containerStyle}
        className={this.props.containerClassName}
      >
        {this.props.children}
      </div>
    );
  }

  renderContent() {
    /* eslint-disable no-unused-vars */
    const {
      hide, children, containerStyle, containerClassName, name, noExpandX, onClick, zIndex, ...rest
    } = this.props;

    const customClass = this.props.fromTop ? 'options-list--top' : '';

    /* eslint-enable no-unused-vars */
    ReactDOM.unstable_renderSubtreeIntoContainer(
      this,
      <OptionsList {...rest} fromTopClassName={customClass} name={name} onClickOutside={this.onClickOutside} />,
      this.containerElement,
    );

    /*
     *  In case the fromTop option is set, we need to render the options list above the options
     *  trigger element. In order to do this, we need to know the height of the options list, which
     *  we only know after we actually render it, that's why we set its position at this point.
     */
    if (this.props.fromTop) {
      setTimeout(() => {
        const windowRect = document.documentElement.getBoundingClientRect();
        const contentRect = this.content.getBoundingClientRect();
        const optionsRect = this.containerElement.getBoundingClientRect();

        this.containerElement.style.top = `${contentRect.top - optionsRect.height - windowRect.top - 5}px`;
        this.containerElement.style.display = 'block';
      }, 0);
    } else {
      this.containerElement.style.display = 'block';
    }
  }
}

export default OptionsListContainer;



// WEBPACK FOOTER //
// ./spa/Components/OptionsList.jsx